import Tooltip from '@mui/material/Tooltip';
import {
  Box,
  Card,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import {
  toggleCostVehicleTypeDependence,
  updateCostSettings,
} from 'store/reducers/inputReducer.js';

import {
  selectActiveModelSettings,
  selectCostSetting,
  selectTableLength,
  selectUniqueColumnValues
} from 'store/reducers/selectors';

import OptimizationChoice from './OptimizationChoice';
import InfoIcon from 'components/common/icons/InfoIcon';

const parseMultiplier = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return null;
  return parsed;
};

const CostMultiplierForm = () => {
  const dispatch = useDispatch();

  const costMetric = useSelector(selectCostSetting({ settingName: 'costMetric' }));
  const multiplierSettings = useSelector(selectCostSetting({ settingName: 'costMultiplier' }));
  const autoCompute = useSelector(selectCostSetting({ settingName: 'autoCompute', subSettingName: costMetric }));
  const vehicleTypeDependence = useSelector(selectCostSetting({ settingName: 'vehicleTypeDependence' }));

  const vehicleTypes = useSelector(selectUniqueColumnValues({ tableName: 'vehicle_types', columnNames: ['vehicleType', 'UUID'] }));
  const vehicleTypesActive = useSelector(selectActiveModelSettings).input.enabled['vehicle_types'];
  const numVehicleTypes = useSelector(selectTableLength('vehicle_types'));

  const setMultiplier = (newMultiplier, vehicleType) => {
    if (newMultiplier === null) return;

    if (!vehicleType) {
      dispatch(
        updateCostSettings({
          settingName: 'costMultiplier',
          subsettingName: 'all',
          value: newMultiplier,
        })
      );
      return;
    }

    dispatch(
      updateCostSettings({
        settingName: 'costMultiplier',
        subsettingName: 'vehicleTypes',
        subSubsettingName: vehicleType,
        value: newMultiplier,
      })
    );
  };

  const handleVehicleTypeDependenceChange = () => {
    dispatch(toggleCostVehicleTypeDependence(!vehicleTypeDependence));
  };

  const showMultiplierControls = costMetric !== 'custom' && autoCompute;

  return (
    <Stack spacing={2.25} sx={{ pt: 0.5 }}>
      <OptimizationChoice />

      {costMetric !== 'custom' && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2.5,
            borderColor: 'divider',
            boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
          }}
        >
          <Stack spacing={1.25} sx={{ p: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'dark.main' }}>
                {costMetric === 'distance' ? 'Distance multiplier' : 'Duration multiplier'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Adjust cost per unit. For example, set to 2 to represent $2 per unit of distance (or per minute).
              </Typography>
            </Box>

            {vehicleTypesActive && (
              <Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Costs vary by vehicle type
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Add separate multipliers for each vehicle type.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Switch
                      disabled={numVehicleTypes < 1}
                      checked={Boolean(numVehicleTypes < 1 ? false : vehicleTypeDependence)}
                      onChange={handleVehicleTypeDependenceChange}
                    />
                    <Tooltip
                      title={
                        numVehicleTypes < 1
                          ? 'There must be at least one vehicle type to use this setting'
                          :
                            'If vehicle type impacts travel costs between locations, enable this. This will add a Vehicle Type row to the Travel Costs table.'
                      }
                      placement="top"
                    >
                      <Box component="span"><InfoIcon /></Box>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            )}

            {showMultiplierControls && (
              <Stack spacing={1.5}>
                {!vehicleTypeDependence || numVehicleTypes < 1 ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
                      All vehicles
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={multiplierSettings?.all ?? 1}
                        onChange={(e) => {
                          const parsed = parseMultiplier(e.target.value);
                          if (parsed !== null) setMultiplier(parsed);
                        }}
                        inputProps={{ step: 0.1, min: 0 }}
                        sx={{ maxWidth: 200 }}
                      />
                      <Tooltip
                        title="The cost multiplier for the selected metric. For example, to incur a cost of $2 per unit of distance, set the multiplier to 2."
                        placement="top"
                      >
                        <Box component="span"><InfoIcon /></Box>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : null}

                {vehicleTypeDependence && vehicleTypes && (
                  <Stack spacing={1.25}>
                    {vehicleTypes.map(({ vehicleType, UUID }) => (
                      <Box key={UUID}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
                          {vehicleType}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={multiplierSettings?.vehicleTypes?.[UUID] ?? 1}
                            onChange={(e) => {
                              const parsed = parseMultiplier(e.target.value);
                              if (parsed !== null) setMultiplier(parsed, UUID);
                            }}
                            inputProps={{ step: 0.1, min: 0 }}
                            sx={{ maxWidth: 200 }}
                          />
                          <Tooltip
                            title={`The cost multiplier for all vehicles of type ${vehicleType}. For example, to incur a cost of $2 per unit of distance, set the multiplier to 2.`}
                            placement="top"
                          >
                            <Box component="span"><InfoIcon /></Box>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}

                {!autoCompute && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Multipliers are disabled when costs are set to manual entry.
                  </Typography>
                )}
              </Stack>
            )}

            {!autoCompute && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Enable auto-compute to adjust multipliers.
              </Typography>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

export default CostMultiplierForm;
